import React, { useState, useCallback, useEffect, useMemo } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  MenuItem,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import MultiRfidSearchComponent from '../components/MultiRfidSearchComponent';
import { CoralResponse, CoralFormData } from '../types';
import { listCorals, deleteCoral, updateCoral, getCoral, getCoralByRfid } from '../services/api';
import RefreshIcon from '@mui/icons-material/Refresh';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import SearchIcon from '@mui/icons-material/Search';
import { Link as RouterLink } from 'react-router-dom';
import { brand } from '../theme';

const ViewCorals: React.FC = () => {
  const [corals, setCorals] = useState<CoralResponse[]>([]);
  const [filteredCorals, setFilteredCorals] = useState<CoralResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [cursorHistory, setCursorHistory] = useState<string[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [coralToDelete, setCoralToDelete] = useState<CoralResponse | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [coralToEdit, setCoralToEdit] = useState<CoralResponse | null>(null);
  const [updating, setUpdating] = useState(false);
  const [editFormData, setEditFormData] = useState<CoralFormData>({
    speciesCode: '',
    cutDate: '',
    condition: 'ALIVE',
  });
  const [searching, setSearching] = useState(false);
  const [lookupMode, setLookupMode] = useState<'rfid' | 'coralId' | 'filter'>('rfid');
  const [searchMode, setSearchMode] = useState<'all' | 'rfid' | 'coralId'>('all');
  const [selectedRfids, setSelectedRfids] = useState<string[]>([]);
  const [coralIdInput, setCoralIdInput] = useState('');
  const [selectedCoralId, setSelectedCoralId] = useState('');
  const [speciesCodeFilter, setSpeciesCodeFilter] = useState('');
  const [rfidAttachedFilter, setRfidAttachedFilter] = useState<'all' | 'yes' | 'no'>('all');

  // Filters only exist in "Filter" mode and run on the server so they cover every page.
  const [debouncedSpecies, setDebouncedSpecies] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSpecies(speciesCodeFilter.trim()), 400);
    return () => clearTimeout(timer);
  }, [speciesCodeFilter]);
  const serverFilters = useMemo(
    () => ({
      speciesCode: debouncedSpecies || undefined,
      rfidAttached: rfidAttachedFilter === 'all' ? undefined : rfidAttachedFilter === 'yes',
    }),
    [debouncedSpecies, rfidAttachedFilter]
  );

  const filtersActive = speciesCodeFilter.trim() !== '' || rfidAttachedFilter !== 'all';

  // Fetch initial coral data from API
  useEffect(() => {
    const fetchInitialCorals = async () => {
      try {
        setLoading(true);
        console.log('Fetching initial corals from API...');

        const response = await listCorals(200, undefined, undefined, serverFilters);
        console.log(
          'Received initial coral data:',
          response.items?.length,
          'nextCursor:',
          response.nextCursor
        );

        setCorals(response.items || []);
        setFilteredCorals(response.items || []);
        setNextCursor(response.nextCursor || null);
        setError(null);
      } catch (err: any) {
        console.error('Error fetching corals:', err);

        let errorMessage = 'Failed to load coral data';

        if (err.response?.data?.errors) {
          errorMessage = `Loading failed: ${err.response.data.errors}`;
        } else if (err.response?.status === 403) {
          errorMessage = 'Access denied. You may not have permission to view coral data.';
        } else if (err.response?.status === 401) {
          errorMessage = 'Authentication required. Please log in and try again.';
        } else if (err.message) {
          errorMessage = `Loading failed: ${err.message}`;
        }

        setError(errorMessage);
        setCorals([]);
        setFilteredCorals([]);
      } finally {
        setLoading(false);
      }
    };

    // Scan / Coral ID results are filtered client-side; reload the list when returning to it
    if (searchMode !== 'all') return;
    setCursorHistory([]);
    fetchInitialCorals();
  }, [serverFilters, searchMode]);

  const handleRefresh = async () => {
    try {
      setRefreshing(true);
      setError(null);
      setSearchMode('all');
      setSelectedRfids([]);
      setSelectedCoralId('');
      setCursorHistory([]);
      setNextCursor(null);
      console.log('Refreshing coral data...');

      const response = await listCorals(200, undefined, undefined, serverFilters);
      console.log(
        'Refreshed coral data:',
        response.items?.length,
        'nextCursor:',
        response.nextCursor
      );

      setCorals(response.items || []);
      setFilteredCorals(response.items || []);
      setNextCursor(response.nextCursor || null);
    } catch (err: any) {
      console.error('Error refreshing corals:', err);

      let errorMessage = 'Failed to refresh coral data';

      if (err.response?.data?.errors) {
        errorMessage = `Refresh failed: ${err.response.data.errors}`;
      } else if (err.message) {
        errorMessage = `Refresh failed: ${err.message}`;
      }

      setError(errorMessage);
    } finally {
      setRefreshing(false);
    }
  };

  const handleNextPage = async () => {
    if (!nextCursor || searchMode !== 'all') return;

    try {
      setLoading(true);
      console.log('Loading next page with cursor:', nextCursor);

      const response = await listCorals(200, nextCursor, undefined, serverFilters);
      console.log('Loaded next page:', response.items?.length, 'nextCursor:', response.nextCursor);

      setCorals(response.items || []);
      setFilteredCorals(response.items || []);

      // Add current cursor to history before updating
      setCursorHistory((prev) => [...prev, nextCursor]);
      setNextCursor(response.nextCursor || null);
    } catch (err: any) {
      console.error('Error loading next page:', err);

      let errorMessage = 'Failed to load next page of coral data';

      if (err.response?.data?.errors) {
        errorMessage = `Pagination failed: ${err.response.data.errors}`;
      } else if (err.message) {
        errorMessage = `Pagination failed: ${err.message}`;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handlePrevPage = async () => {
    if (cursorHistory.length === 0) {
      // Go to first page
      setCursorHistory([]);
      await handleRefresh();
      return;
    }

    try {
      setLoading(true);
      const prevCursor = cursorHistory[cursorHistory.length - 2];
      console.log('Loading previous page with cursor:', prevCursor);

      const response = await listCorals(200, prevCursor, undefined, serverFilters);
      console.log(
        'Loaded previous page:',
        response.items?.length,
        'nextCursor:',
        response.nextCursor
      );

      setCorals(response.items || []);
      setFilteredCorals(response.items || []);

      // Remove last cursor from history
      setCursorHistory((prev) => prev.slice(0, -1));
      setNextCursor(response.nextCursor || null);
    } catch (err: any) {
      console.error('Error loading previous page:', err);

      let errorMessage = 'Failed to load previous page of coral data';

      if (err.response?.data?.errors) {
        errorMessage = `Pagination failed: ${err.response.data.errors}`;
      } else if (err.message) {
        errorMessage = `Pagination failed: ${err.message}`;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (coral: CoralResponse) => {
    setCoralToEdit(coral);
    setEditFormData({
      speciesCode: coral.speciesCode,
      cutDate: coral.cutDate || '',
      condition: coral.status,
    });
    setEditDialogOpen(true);
  };

  const handleEditCancel = () => {
    setEditDialogOpen(false);
    setCoralToEdit(null);
  };

  const handleEditSave = async () => {
    if (!coralToEdit) return;

    try {
      setUpdating(true);
      console.log('Updating coral:', coralToEdit.coralId, 'with data:', editFormData);

      const updatePayload = {
        speciesCode: editFormData.speciesCode,
        cutDate: editFormData.cutDate || undefined,
        status: editFormData.condition,
      };

      const updatedCoral = await updateCoral(coralToEdit.coralId, updatePayload);
      // console.log('Received updated coral:', updatedCoral);

      // Verify the updated coral has all required fields
      if (!updatedCoral || !updatedCoral.coralId) {
        throw new Error('Invalid response from update API');
      }

      // Update the coral in both arrays
      const updatedCorals = corals.map((coral) =>
        coral.coralId === coralToEdit.coralId ? updatedCoral : coral
      );
      setCorals(updatedCorals);

      // Update filtered corals if the updated coral should still be visible
      const updatedFilteredCorals = filteredCorals.map((coral) =>
        coral.coralId === coralToEdit.coralId ? updatedCoral : coral
      );
      setFilteredCorals(updatedFilteredCorals);

      setEditDialogOpen(false);
      setCoralToEdit(null);
    } catch (err: any) {
      console.error('Error updating coral:', err);

      let errorMessage = 'Failed to update coral fragment';

      if (err.response?.data?.errors) {
        errorMessage = `Update failed: ${err.response.data.errors}`;
      } else if (err.response?.status === 404) {
        errorMessage = 'Coral fragment not found. It may have been deleted by another user.';
      } else if (err.response?.status === 409) {
        errorMessage = 'Update failed: RFID already exists in the system.';
      } else if (err.message) {
        errorMessage = `Update failed: ${err.message}`;
      }

      setError(errorMessage);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteClick = (coral: CoralResponse) => {
    setCoralToDelete(coral);
    setDeleteDialogOpen(true);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setCoralToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!coralToDelete) return;

    try {
      setDeleting(true);
      console.log('Deleting coral:', coralToDelete.coralId);

      await deleteCoral(coralToDelete.coralId);

      // Remove the deleted coral from both arrays
      const updatedCorals = corals.filter((coral) => coral.coralId !== coralToDelete.coralId);
      setCorals(updatedCorals);
      setFilteredCorals(
        updatedCorals.filter((coral) =>
          filteredCorals.some((filtered) => filtered.coralId === coral.coralId)
        )
      );

      setDeleteDialogOpen(false);
      setCoralToDelete(null);
    } catch (err: any) {
      console.error('Error deleting coral:', err);

      let errorMessage = 'Failed to delete coral fragment';

      if (err.response?.data?.errors) {
        errorMessage = `Delete failed: ${err.response.data.errors}`;
      } else if (err.response?.status === 404) {
        errorMessage = 'Coral fragment not found. It may have already been deleted.';
      } else if (err.message) {
        errorMessage = `Delete failed: ${err.message}`;
      }

      setError(errorMessage);
    } finally {
      setDeleting(false);
    }
  };

  const handleSearch = useCallback(
    async (rfids: string[]) => {
      if (!rfids || rfids.length === 0) {
        // If empty search, show all corals
        setFilteredCorals(corals);
        setSearchMode('all');
        setSelectedRfids([]);
        return;
      }

      try {
        setSearching(true);
        setError(null);
        console.log('Searching for RFIDs:', rfids);

        // Search for each RFID and collect results
        const results: CoralResponse[] = [];

        for (const rfid of rfids) {
          try {
            const result = await getCoralByRfid(rfid.trim());
            if (result) {
              results.push(result);
            }
          } catch (err: any) {
            console.warn(`RFID ${rfid} not found:`, err.message);
            // Continue searching for other RFIDs even if one is not found
          }
        }

        setFilteredCorals(results);
        setSearchMode('rfid');
        setSelectedRfids(rfids);
      } catch (err: any) {
        console.error('Error searching by RFIDs:', err);

        let errorMessage = 'Search operation failed';

        if (err.response?.data?.errors) {
          errorMessage = `Search failed: ${err.response.data.errors}`;
        } else if (err.message) {
          errorMessage = `Search failed: ${err.message}`;
        }

        setError(errorMessage);
        setSearchMode('rfid');
      } finally {
        setSearching(false);
      }
    },
    [corals]
  );

  const handleCoralIdSearch = useCallback(async () => {
    const coralId = coralIdInput.trim();
    if (!coralId) {
      return;
    }

    try {
      setSearching(true);
      setError(null);
      const result = await getCoral(coralId);
      setFilteredCorals(result ? [result] : []);
      setSearchMode('coralId');
      setSelectedCoralId(coralId);
    } catch (err: any) {
      setFilteredCorals([]);
      setSearchMode('coralId');
      setSelectedCoralId(coralId);
      setError(err?.message || 'Coral not found');
    } finally {
      setSearching(false);
    }
  }, [coralIdInput]);

  const handleShowAll = useCallback(() => {
    setFilteredCorals(corals);
    setSearchMode('all');
    setSelectedRfids([]);
    setSelectedCoralId('');
    setCoralIdInput('');
    setSpeciesCodeFilter('');
    setDebouncedSpecies('');
    setRfidAttachedFilter('all');
    setError(null);
  }, [corals]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ALIVE':
        return 'success';
      case 'DECEASED':
        return 'error';
      case 'EXPORTED':
        return 'info';
      default:
        return 'default';
    }
  };

  // dd/mm/yyyy hh:mm (24h), matching the report format and keeping the column narrow
  const formatDate = (dateString: string) => {
    const d = new Date(dateString);
    if (Number.isNaN(d.getTime())) return '-';
    return d.toLocaleString('en-AU', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    });
  };

  return (
    <Container maxWidth={false} sx={{ py: 3, px: { xs: 2, md: 3 } }}>
      {/* Search Section */}
      <Paper
        variant="outlined"
        sx={{ p: 2, mb: 2, boxShadow: 1, borderTop: `4px solid ${brand.coral}` }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: '1fr auto 1fr',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Box />
          <Typography variant="h6" sx={{ textAlign: 'center', color: brand.navy, fontWeight: 700 }}>
            Search Corals
          </Typography>
          <Box sx={{ justifySelf: 'end' }}>
            {(searchMode !== 'all' || filtersActive) && (
              <Button variant="outlined" size="small" onClick={handleShowAll} disabled={searching}>
                Show All
              </Button>
            )}
          </Box>
        </Box>
        <Stack spacing={2}>
          <ToggleButtonGroup
            value={lookupMode}
            exclusive
            onChange={(_, value: 'rfid' | 'coralId' | 'filter' | null) => {
              if (!value || value === lookupMode) return;
              setLookupMode(value);
              handleShowAll(); // each mode starts from the full list
            }}
            size="small"
            aria-label="coral lookup mode"
            sx={{
              alignSelf: 'flex-start',
              '& .MuiToggleButton-root': { px: 2.5, fontWeight: 600 },
              '& .MuiToggleButton-root.Mui-selected, & .MuiToggleButton-root.Mui-selected:hover': {
                bgcolor: brand.navy,
                color: '#fff',
              },
            }}
          >
            <ToggleButton value="rfid">RFID Scan</ToggleButton>
            <ToggleButton value="coralId">Coral ID</ToggleButton>
            <ToggleButton value="filter">Filter</ToggleButton>
          </ToggleButtonGroup>
          {lookupMode === 'rfid' ? (
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Scan or enter multiple RFID codes to search for coral fragments.
              </Typography>
              <MultiRfidSearchComponent onSearch={handleSearch} isSearching={searching} />
            </Stack>
          ) : lookupMode === 'coralId' ? (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Internal Coral ID"
                placeholder="Enter CoralID"
                value={coralIdInput}
                onChange={(event) => setCoralIdInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void handleCoralIdSearch();
                  }
                }}
                disabled={searching}
                size="small"
                fullWidth
              />
              <Button
                variant="contained"
                onClick={() => void handleCoralIdSearch()}
                disabled={searching || !coralIdInput.trim()}
                startIcon={searching ? <CircularProgress size={16} /> : <SearchIcon />}
              >
                Search
              </Button>
            </Stack>
          ) : (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <TextField
                label="Species Code"
                placeholder="e.g. ABO"
                value={speciesCodeFilter}
                onChange={(event) => setSpeciesCodeFilter(event.target.value)}
                size="small"
                fullWidth
              />
              <TextField
                select
                label="RFID tag attached"
                value={rfidAttachedFilter}
                onChange={(event) =>
                  setRfidAttachedFilter(event.target.value as 'all' | 'yes' | 'no')
                }
                size="small"
                sx={{ minWidth: 200 }}
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="yes">Yes</MenuItem>
                <MenuItem value="no">No</MenuItem>
              </TextField>
            </Stack>
          )}
        </Stack>
        {searching && (
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
            <CircularProgress size={16} sx={{ mr: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Searching...
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Results Section */}
      <Paper variant="outlined" sx={{ p: 2, boxShadow: 1, borderTop: `4px solid ${brand.navy}` }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 2,
          }}
        >
          <Typography variant="h6">
            {searchMode !== 'all' || filtersActive
              ? `Search Results (${filteredCorals.length})`
              : `Coral Fragments (${filteredCorals.length})`}
          </Typography>
          <Tooltip title="Refresh data">
            <IconButton onClick={handleRefresh} disabled={loading || refreshing} size="small">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table
              stickyHeader
              size="small"
              sx={{
                // keep each value on one line; the container scrolls sideways on narrow screens
                '& .MuiTableCell-root': { whiteSpace: 'nowrap', px: 1.5, py: 1.25 },
                '& .MuiTableCell-head': { py: 1.5 },
                '& .MuiTableBody-root .MuiTableRow-root:nth-of-type(even)': {
                  bgcolor: brand.navyLight,
                },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>RFID</TableCell>
                  <TableCell>Coral ID</TableCell>
                  <TableCell>Species Code</TableCell>
                  <TableCell>Cut Date</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Updated</TableCell>
                  <TableCell align="center" sx={{ minWidth: 132 }}>
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCorals.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                      {filtersActive
                        ? 'No coral fragments match the selected filters.'
                        : searchMode === 'rfid'
                          ? selectedRfids.length > 1
                            ? `No coral fragments found for the selected ${selectedRfids.length} RFIDs.`
                            : selectedRfids.length === 1
                              ? `No coral fragment found with RFID: ${selectedRfids[0]}`
                              : 'No results found.'
                          : searchMode === 'coralId'
                            ? `No coral fragment found with CoralID: ${selectedCoralId}`
                            : corals.length === 0
                              ? 'No coral fragments found.'
                              : 'No matches found for your search.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredCorals.map((coral) => (
                    <TableRow
                      key={coral.coralId}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={(event) => {
                        if ((event.target as HTMLElement).closest('button')) {
                          return;
                        }
                        window.location.href = `/coral/view/${coral.coralId}`;
                      }}
                    >
                      <TableCell sx={{ fontWeight: 'medium' }}>{coral.rfid}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {coral.coralId}
                      </TableCell>
                      <TableCell>{coral.speciesCode}</TableCell>
                      <TableCell>{coral.cutDate || '-'}</TableCell>
                      <TableCell>
                        <Chip
                          label={coral.status}
                          color={getStatusColor(coral.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{formatDate(coral.createdAt)}</TableCell>
                      <TableCell>{formatDate(coral.updatedAt)}</TableCell>
                      <TableCell align="center" sx={{ minWidth: 132 }}>
                        <Box
                          sx={{
                            display: 'flex',
                            flexWrap: 'nowrap',
                            justifyContent: 'center',
                            alignItems: 'center',
                            gap: 0.5,
                            '& > *': { flexShrink: 0 },
                          }}
                        >
                          <Tooltip title="Edit coral">
                            <IconButton
                              onClick={(event) => {
                                event.stopPropagation();
                                handleEditClick(coral);
                              }}
                              color="primary"
                              size="small"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View details">
                            <IconButton
                              component={RouterLink}
                              to={`/coral/view/${coral.coralId}`}
                              color="secondary"
                              size="small"
                              onClick={(event) => event.stopPropagation()}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete coral">
                            <IconButton
                              onClick={(event) => {
                                event.stopPropagation();
                                handleDeleteClick(coral);
                              }}
                              color="error"
                              size="small"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {/* Pagination Controls */}
        {searchMode === 'all' && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mt: 4,
            }}
          >
            <Button
              variant="outlined"
              onClick={handlePrevPage}
              disabled={loading || cursorHistory.length === 0}
              size="small"
            >
              Previous Page
            </Button>
            <Typography variant="body2" color="text.secondary">
              {(() => {
                const currentPage = cursorHistory.length + 1;
                const startRecord = (currentPage - 1) * 200 + 1;
                const endRecord = startRecord + filteredCorals.length - 1;
                return `Showing ${startRecord}-${endRecord} (Page ${currentPage})`;
              })()}
            </Typography>
            <Button
              variant="outlined"
              onClick={handleNextPage}
              disabled={loading || !nextCursor}
              size="small"
            >
              Next Page
            </Button>
          </Box>
        )}
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">Delete Coral Fragment</DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this coral fragment?
          </DialogContentText>
          <Box sx={{ my: 2 }}>
            <Typography variant="body2">
              <Typography component="span" fontWeight="bold">
                RFID:
              </Typography>{' '}
              {coralToDelete?.rfid}
            </Typography>
            <Typography variant="body2">
              <Typography component="span" fontWeight="bold">
                Species:
              </Typography>{' '}
              {coralToDelete?.speciesCode}
            </Typography>
            <Typography variant="body2">
              <Typography component="span" fontWeight="bold">
                Cut Date:
              </Typography>{' '}
              {coralToDelete?.cutDate || '-'}
            </Typography>
          </Box>
          <DialogContentText>This action cannot be undone.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} /> : <DeleteIcon />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Coral Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={handleEditCancel}
        aria-labelledby="edit-dialog-title"
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle id="edit-dialog-title">Edit Coral Fragment</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            <Typography component="span" fontWeight="bold">
              RFID:
            </Typography>{' '}
            {coralToEdit?.rfid}
          </DialogContentText>
          <Stack spacing={2} sx={{ mt: 2 }}>
            <TextField
              label="Species Code"
              value={editFormData.speciesCode}
              onChange={(e) =>
                setEditFormData({
                  ...editFormData,
                  speciesCode: e.target.value,
                })
              }
              disabled={updating}
              fullWidth
            />
            <TextField
              label="Cut Date"
              placeholder="YYYY-MM (e.g., 2025-11)"
              value={editFormData.cutDate}
              onChange={(e) => setEditFormData({ ...editFormData, cutDate: e.target.value })}
              disabled={updating}
              fullWidth
              helperText="Optional: Format YYYY-MM. Defaults to current month if not provided."
            />
            <TextField
              select
              label="Status"
              value={editFormData.condition}
              onChange={(e) =>
                setEditFormData({
                  ...editFormData,
                  condition: e.target.value as 'ALIVE' | 'DECEASED' | 'EXPORTED',
                })
              }
              disabled={updating}
              fullWidth
            >
              <MenuItem value="ALIVE">Alive</MenuItem>
              <MenuItem value="DECEASED">Deceased</MenuItem>
              <MenuItem value="EXPORTED">Exported</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditCancel} disabled={updating}>
            Cancel
          </Button>
          <Button
            onClick={handleEditSave}
            variant="contained"
            disabled={updating}
            startIcon={updating ? <CircularProgress size={16} /> : <EditIcon />}
          >
            {updating ? 'Updating...' : 'Update'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ViewCorals;
